import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import axiosClient from "../../../api/api";
import { BsTag, BsCheck2Circle, BsClockHistory, BsX } from "react-icons/bs";
import {
  successToast,
  errorToast,
} from "../../../adminPanel/components/toastAlerts/ToastAlerts";

/**
 * Settings-page promocode widget:
 *   - Fetches /my-promo on mount to show the current binding + effective
 *     window ("აქტიური until 31.12.2026").
 *   - Lets the user enter a new promo NAME to overwrite the current binding.
 *   - Lets the user detach the current binding.
 * Only ONE promo is active per account (backend enforces via users.promo_id
 * being a single foreign key).
 */
const PromoCodeSection = ({ prefillUser }) => {
  const { t } = useTranslation();

  const [state, setState] = useState({
    promo: prefillUser?.promokodi || null,
    active: null,
    loading: true,
  });
  const [entry, setEntry] = useState("");
  const [applying, setApplying] = useState(false);

  const load = () => {
    setState((s) => ({ ...s, loading: true }));
    axiosClient
      .get("/my-promo")
      .then(({ data }) =>
        setState({ promo: data.promo, active: data.active, loading: false })
      )
      .catch(() => setState((s) => ({ ...s, loading: false })));
  };

  useEffect(() => {
    load();

  }, []);

  const apply = () => {
    const name = entry.trim();
    if (!name) return;
    setApplying(true);
    axiosClient
      .post("/my-promo/apply", { name })
      .then(({ data }) => {
        setState({ promo: data.promo, active: data.active, loading: false });
        setEntry("");
        successToast(t("promo_applied") || "პრომოკოდი გააქტიურდა");
      })
      .catch((err) => {
        const code = err?.response?.status;
        if (code === 404) errorToast(t("promo_not_found") || "პრომოკოდი ვერ მოიძებნა");
        else if (code === 410) errorToast(t("promo_expired") || "პრომოკოდის ვადა ამოწურულია");
        else errorToast(t("promo_apply_error") || "შეცდომა — სცადეთ თავიდან");
      })
      .finally(() => setApplying(false));
  };

  const remove = () => {
    axiosClient
      .post("/my-promo/remove")
      .then(() => {
        setState({ promo: null, active: null, loading: false });
        successToast(t("promo_removed") || "პრომოკოდი მოშორდა");
      })
      .catch(() =>
        errorToast(t("promo_apply_error") || "შეცდომა — სცადეთ თავიდან")
      );
  };

  // Format date-only from an ISO string. Falls back to "-" when missing.
  const fmt = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return String(iso).slice(0, 10);
    return `${String(d.getDate()).padStart(2, "0")}.${String(
      d.getMonth() + 1
    ).padStart(2, "0")}.${d.getFullYear()}`;
  };

  return (
    <div className="shadow-bs1 rounded-[30px] sm:rounded-lg mt-5 bg-white">
      <div className="p-5 border-b border-gray-200 flex items-center gap-3">
        <BsTag className="w-8 h-8 text-teal_600" />
        <div>
          <p className="font-semibold text-base md:text-sm">
            {t("promo_code") || "პრომოკოდი"}
          </p>
          <p className="text-xs text-gray-500">
            {t("promo_one_at_a_time") ||
              "ერთდროულად მხოლოდ ერთი პრომოკოდი შეიძლება იყოს გააქტიურებული."}
          </p>
        </div>
      </div>

      <div className="p-5 flex flex-col gap-4">
        {/* Current binding — badge, percent, dates. */}
        {state.loading ? (
          <div className="animate-pulse h-16 rounded-xl bg-gray-100" />
        ) : state.promo ? (
          <div className="rounded-xl border border-teal-100 bg-teal-50 p-4 flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2 min-w-0">
              {state.active ? (
                <BsCheck2Circle className="text-teal-700 w-6 h-6 shrink-0" />
              ) : (
                <BsClockHistory className="text-amber-600 w-6 h-6 shrink-0" />
              )}
              <div className="min-w-0">
                <p className="font-bold text-teal-900 text-sm truncate">
                  {state.promo.name}
                </p>
                <p className="text-[11px] text-teal-800">
                  {state.active
                    ? t("promo_active") || "აქტიური"
                    : t("promo_inactive") || "ვადა ამოწურულია"}
                  {" · "}
                  {state.promo.percent}%
                </p>
              </div>
            </div>
            <div className="text-[11px] text-slate-600 leading-tight">
              <div>
                <b>{t("promo_from") || "დან"}:</b> {fmt(state.promo.date_from)}
              </div>
              <div>
                <b>{t("promo_to") || "მდე"}:</b> {fmt(state.promo.date_to)}
              </div>
            </div>
            <button
              type="button"
              onClick={remove}
              className="ml-auto flex items-center gap-1 text-xs text-red-500
                         hover:bg-red-50 rounded-full px-3 py-1 font-semibold"
            >
              <BsX className="text-lg" />
              {t("promo_remove") || "მოშორება"}
            </button>
          </div>
        ) : (
          <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm text-gray-500">
            {t("promo_none") ||
              "ამჟამად პრომოკოდი გააქტიურებული არ გაქვთ."}
          </div>
        )}

        {/* Entry — apply a new promo (overwrites current). */}
        <div className="flex flex-col gap-2">
          <label className="text-xs font-bold text-slate-600">
            {state.promo
              ? t("promo_replace_hint") || "ახალი პრომოკოდი (ჩაანაცვლებს არსებულს)"
              : t("promo_enter_hint") || "შეიყვანე პრომოკოდი"}
          </label>
          <div className="flex gap-2 flex-wrap">
            <input
              type="text"
              placeholder={t("promo_placeholder") || "მაგ.: SUMMER25"}
              value={entry}
              onChange={(e) => setEntry(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && apply()}
              className="flex-1 min-w-[10rem] h-10 px-3 border border-gray-300
                         rounded-full uppercase tracking-wide text-sm
                         focus:outline-none focus:border-teal_600"
            />
            <button
              type="button"
              disabled={applying || !entry.trim()}
              onClick={apply}
              className="h-10 rounded-full bg-teal_600 text-white font-bold text-sm
                         px-6 disabled:opacity-40"
            >
              {applying
                ? t("loading") || "..."
                : t("promo_apply") || "გააქტიურება"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PromoCodeSection;
